'use client'

import { useEffect, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { CalendarIcon, Loader2 } from 'lucide-react'
import { DateTime } from 'luxon'
import { useForm } from 'react-hook-form'
import type { Resolver } from 'react-hook-form'

import type { Advert } from '@/core/api/entities/campaign/advert'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/shared/components/atoms/Form'
import { Input } from '@/shared/components/atoms/Input'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/atoms/Popover'
import { Separator } from '@/shared/components/atoms/Separator'
import { Textarea } from '@/shared/components/atoms/TextArea'
import {
	BottomSheet,
	BottomSheetContent,
	BottomSheetTitle,
} from '@/shared/components/molecules/BottomSheet/BottomSheet'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { Calendar } from '@/shared/components/organisms/Calendar/Calendar'

import {
	createEditAdvertSchema,
	type EditAdvertFormValues,
} from '../schemas/createCampaignSchema'

interface EditAdvertModalProps {
	open: boolean
	advert: Advert | null
	isSaving: boolean
	onOpenChange: (open: boolean) => void
	onSave: (advertId: number, values: EditAdvertFormValues) => void
}

export function EditAdvertModal({
	open,
	advert,
	isSaving,
	onOpenChange,
	onSave,
}: EditAdvertModalProps) {
	const { t } = useTranslation('campaigns')
	const [isDesktop, setIsDesktop] = useState(
		() => !window.matchMedia('(max-width: 767px)').matches
	)

	useEffect(() => {
		const mediaQuery = window.matchMedia('(max-width: 767px)')
		const handler = (e: MediaQueryListEvent) => setIsDesktop(!e.matches)
		mediaQuery.addEventListener('change', handler)
		return () => mediaQuery.removeEventListener('change', handler)
	}, [])

	const form = useForm<EditAdvertFormValues>({
		resolver: yupResolver(
			createEditAdvertSchema(t)
		) as Resolver<EditAdvertFormValues>,
		defaultValues: {
			advertName: '',
			description: '',
			adStartDate: undefined,
			adEndDate: undefined,
		},
	})

	useEffect(() => {
		if (open && advert) {
			form.reset({
				advertName: advert.name,
				description: advert.description ?? '',
				adStartDate: advert.dateStart?.toJSDate() ?? undefined,
				adEndDate: advert.dateEnd?.toJSDate() ?? undefined,
			})
		}
	}, [open, advert, form])

	const handleSubmit = (values: EditAdvertFormValues) => {
		if (!advert) return
		onSave(advert.id, values)
	}

	const handleOpenChange = (next: boolean) => {
		if (!next) {
			form.reset()
		}
		onOpenChange(next)
	}

	const content = (
		<Form {...form}>
			<form
				id="edit-advert-form"
				onSubmit={form.handleSubmit(handleSubmit)}
				className="space-y-4 px-5 py-4"
			>
				<FormField
					control={form.control}
					name="advertName"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('modals.editAd.advertName')}</FormLabel>
							<FormControl>
								<Input
									placeholder={t('modals.editAd.advertNamePlaceholder')}
									{...field}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="description"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('modals.editAd.description')}</FormLabel>
							<FormControl>
								<Textarea
									placeholder={t('modals.editAd.descriptionPlaceholder')}
									{...field}
									value={field.value ?? ''}
									className="min-h-[90px] bg-background border-border text-foreground resize-none"
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<div className="grid grid-cols-2 gap-3">
					<FormField
						control={form.control}
						name="adStartDate"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('modals.editAd.adStartDate')}</FormLabel>
								<Popover>
									<PopoverTrigger asChild>
										<FormControl>
											<Button
												type="button"
												variant="secondary"
												className="w-full justify-start font-normal"
											>
												<CalendarIcon className="mr-2 h-4 w-4" />
												{field.value ? (
													DateTime.fromJSDate(field.value).toFormat(
														'EEE d LLL, yyyy'
													)
												) : (
													<span>{t('modals.editAd.pickDate')}</span>
												)}
											</Button>
										</FormControl>
									</PopoverTrigger>
									<PopoverContent className="w-auto p-0">
										<Calendar
											mode="single"
											selected={field.value ?? undefined}
											onSelect={field.onChange}
										/>
									</PopoverContent>
								</Popover>
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="adEndDate"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('modals.editAd.adEndDate')}</FormLabel>
								<Popover>
									<PopoverTrigger asChild>
										<FormControl>
											<Button
												type="button"
												variant="secondary"
												className="w-full justify-start font-normal"
											>
												<CalendarIcon className="mr-2 h-4 w-4" />
												{field.value ? (
													DateTime.fromJSDate(field.value).toFormat(
														'EEE d LLL, yyyy'
													)
												) : (
													<span>{t('modals.editAd.pickDate')}</span>
												)}
											</Button>
										</FormControl>
									</PopoverTrigger>
									<PopoverContent className="w-auto p-0">
										<Calendar
											mode="single"
											selected={field.value ?? undefined}
											onSelect={field.onChange}
										/>
									</PopoverContent>
								</Popover>
							</FormItem>
						)}
					/>
				</div>

				{form.formState.errors.adEndDate && (
					<p className="text-sm font-medium text-destructive">
						{form.formState.errors.adEndDate.message}
					</p>
				)}
			</form>
		</Form>
	)

	if (isDesktop) {
		return (
			<Dialog open={open} onOpenChange={handleOpenChange}>
				<DialogContent className="max-w-lg p-0 gap-0">
					<DialogHeader className="px-5 pt-5 pb-4">
						<DialogTitle>{t('modals.editAd.title')}</DialogTitle>
					</DialogHeader>
					<Separator />
					{content}
					<Separator />
					<DialogFooter className="px-5 py-4 flex flex-row justify-end gap-3">
						<Button
							type="button"
							variant="outline"
							onClick={() => handleOpenChange(false)}
							disabled={isSaving}
							className="rounded-full px-6"
						>
							{t('modals.editAd.cancel')}
						</Button>
						<Button
							type="submit"
							form="edit-advert-form"
							disabled={isSaving}
							className="rounded-full px-6"
						>
							{isSaving ? (
								<>
									<Loader2 className="w-4 h-4 mr-2 animate-spin" />
									{t('modals.editAd.saving')}
								</>
							) : (
								t('modals.editAd.save')
							)}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		)
	}

	return (
		<BottomSheet open={open} onOpenChange={handleOpenChange}>
			<BottomSheetContent
				variant="card"
				className="border-border max-h-[92vh] px-0 pb-4"
				aria-describedby={undefined}
			>
				<div className="px-5 pt-4 pb-3">
					<BottomSheetTitle className="text-xl font-semibold">
						{t('modals.editAd.title')}
					</BottomSheetTitle>
				</div>
				<Separator />
				{content}
				<div className="px-5 pt-2">
					<Button
						type="submit"
						form="edit-advert-form"
						disabled={isSaving}
						className="h-12 rounded-full w-full"
					>
						{isSaving ? (
							<>
								<Loader2 className="w-4 h-4 mr-2 animate-spin" />
								{t('modals.editAd.saving')}
							</>
						) : (
							t('modals.editAd.save')
						)}
					</Button>
				</div>
			</BottomSheetContent>
		</BottomSheet>
	)
}
